import { ref } from "vue";
import { createAnchorForDownload, removeAnchor } from "@/services/DownloadFile";

/**
 * @composable useFileDownload
 * @description Gestisce il download di file con tracking dello stato.
 * @param {Function} downloader - Funzione del service per il download. Deve ritornare { data, error }
 * @param {Object} options - Opzioni di configurazione
 * @param {Function} [options.onError] - Callback eseguito in caso di errore
 * @returns {{
 *   downloadingId: import('vue').Ref<number|null>,
 *   download: Function
 * }}
 */
export function useFileDownload(downloader, { onError } = {}) {
  // ID del file in download; null se nessun download in corso
  const downloadingId = ref(null);

  /**
   * Scarica un file
   * @param {Object} item - Elemento da scaricare (contiene almeno l'id)
   * @param {Object} options - Opzioni per il download
   * @param {*} options.id - ID da usare per tracciare il download
   * @param {Array} options.args - Array di argomenti da passare al downloader
   * @param {string} [options.fileName] - Nome del file da scaricare
   * @returns {Promise<boolean>} true = download riuscito, false = errore. downloadingId torna null in ogni caso (successo, errore restituito o eccezione)
   */
  async function download(item, { id, args, fileName }) {
    downloadingId.value = id;
    try {
      const result = await downloader(...args);
      if (result?.error) {
        console.error(result.error);
        onError?.(result.error);
        return false;
      }
      if (result?.data) {
        const anchor = createAnchorForDownload(result.data, false, fileName);
        anchor.click();
        removeAnchor(anchor);
      }
      return true;
    } catch (err) {
      console.error(err);
      onError?.(err);
      return false;
    } finally {
      // qualunque esito (successo, errore restituito o eccezione): il pulsante torna attivo
      downloadingId.value = null;
    }
  }

  return {
    downloadingId,
    download,
  };
}
